import { Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { DialogService } from 'primeng/dynamicdialog';

import { Observable, Subject, take, takeUntil } from 'rxjs';

import { IPaperDetailsData } from 'editing-tool/src/app/shared/components/paper/steps/details/paper-details-data';
import { IPaperFileHistoryListDialogData } from 'editing-tool/src/app/shared/components/paper/steps/details/paper-file-history-list-dialog/paper-file-history-list-dialog-data';
import { PaperFileHistoryListDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/details/paper-file-history-list-dialog/paper-file-history-list-dialog.component';
import { ValidationConstants, ValidationService, FileResponse } from 'sc-common';
import { ApiEditingToolPaperDetails } from 'sc-common/core/services/open-api/main-api-client';
import { UploadMessage } from 'sc-external/models/upload-message';
import { FileUploaderService } from 'sc-external/services/file-uploader.service';
import { FileUploadComponent } from 'sc-external/shared/components/file-upload/file-upload.component';

@Component({
    selector: 'et-paper-details',
    templateUrl: './paper-details.component.html',
    standalone: false
})
export class PaperDetailsComponent implements OnInit, OnDestroy {

    @Input()
    public data: IPaperDetailsData;

    @Input()
    public isReadonly: boolean;

    @Output()
    public readonly paperNameChanged = new EventEmitter<string>();

    public paperDetails: ApiEditingToolPaperDetails;

    public form: UntypedFormGroup;

    public updateDetailsInProcess: boolean;

    public downloadDocFile$: Observable<FileResponse>;

    public downloadPdfFile$: Observable<FileResponse>;

    public readonly uploadFilesInProcess$ = new Subject<boolean>();

    public readonly uploadFilesProgress$ = new Subject<number>();

    public readonly uploadFilesErrors$ = new Subject<UploadMessage[]>();

    public readonly createFromFilesResponse$ = new Subject<number>();

    public readonly addFilesResponse$ = new Subject<ApiEditingToolPaperDetails>();

    @ViewChild('docUploader')
    private readonly _docUploader: FileUploadComponent;

    @ViewChild('pdfUploader')
    private readonly _pdfUploader: FileUploadComponent;

    @ContentChild(TemplateRef)
    public readonly contentTpl: TemplateRef<any>;

    public externalFormControls: { [item: string]: UntypedFormControl; };

    private _initialFormValue: any = null;

    private readonly _destroy$ = new Subject<void>();

    public get isSubmitDisabled(): boolean {
        return this.form.invalid || !ValidationService.isFormChanged(this._initialFormValue, this.form.value);
    }

    public get canUploadFiles(): boolean {
        return this._docUploader && this._pdfUploader && this._docUploader.isValid() && this._pdfUploader.isValid();
    }

    constructor(
        private readonly _fileUploaderService: FileUploaderService,
        private readonly _router: Router,
        private readonly _route: ActivatedRoute,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _dialogService: DialogService) {
    }

    public ngOnInit(): void {

        this.data.paperDetails$
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                this.paperDetails = result;

                if (this.paperDetails.doc && this.paperDetails.pdf) {
                    this._setDownloadFilesObservables();
                }

                this._buildForm();
            });

        this.createFromFilesResponse$
            .pipe(takeUntil(this._destroy$))
            .subscribe(paperId => {
                this._router.navigate([`../../../${ paperId }/details`], { relativeTo: this._route, replaceUrl: true });
                this._clearFileUploaders();
            });

        this.addFilesResponse$
            .pipe(takeUntil(this._destroy$))
            .subscribe(details => {
                this.paperDetails.init({ ...details, ...this.paperDetails });

                this.form.patchValue({ ...details });
                this._initialFormValue = this.form.value;
                this._setDownloadFilesObservables();
                this.paperNameChanged.emit(this.paperDetails.name);
                this._clearFileUploaders();
            });
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public startUpload(): void {

        const formData = new FormData();
        formData.append('files', this._docUploader.file, this._docUploader.file.name);
        formData.append('files', this._pdfUploader.file, this._pdfUploader.file.name);

        const uploadUrl$ = this.paperDetails
            ? this.data.addFilesUrl$
            : this.data.createFromFilesUrl$;

        const responseResult$ = this.paperDetails
            ? this.addFilesResponse$
            : this.createFromFilesResponse$;

        uploadUrl$
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe(uploadUrl => {
                this._fileUploaderService.startUpload(uploadUrl,
                    formData,
                    this.uploadFilesInProcess$,
                    this.uploadFilesProgress$,
                    this.uploadFilesErrors$,
                    responseResult$);
            });
    }

    public updateDetails(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.updateDetailsInProcess = true;

        this.data.updatePaperDetails(this.form.value)
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe(() => {
                this.updateDetailsInProcess = false;
                this.paperDetails.init({ ...this.paperDetails, ...this.form.value });
                this.paperNameChanged.emit(this.paperDetails.name);
                this._initialFormValue = this.form.value;
            });
    }

    public showFileHistoryListDialog(): void {

        const data: IPaperFileHistoryListDialogData = {
            paperDocuments$: this.data.paperDocuments$,
            downloadDocument: this.data.getDocument
        };

        this._dialogService.open(PaperFileHistoryListDialogComponent, {
            header: $localize`Paper history`,
            closable: true,
            data: data
        });
    }

    private _buildForm(): void {

        if (this.data.formControls) {

            this.externalFormControls = this.data.formControls(this.paperDetails, this.isReadonly);
        }

        this.form = this._formBuilder.group({
            name: [this.paperDetails.name,
            [
                Validators.required,
                Validators.maxLength(ValidationConstants.Max500Length)
            ]],
            abstract: [this.paperDetails.abstract, Validators.required],
            ...this.externalFormControls
        });

        this.form.markAllAsTouched();

        this._initialFormValue = this.form.value;
    }

    private _clearFileUploaders(): void {
        this._docUploader.clearFile();
        this._pdfUploader.clearFile();
    }

    private _setDownloadFilesObservables(): void {
        if (this.paperDetails) {
            this.downloadDocFile$ = this.data.getDocument(this.paperDetails.doc.id);
            this.downloadPdfFile$ = this.data.getDocument(this.paperDetails.pdf.id);
        }
    }
}
